import { useState } from "react"
import { Link } from "@tanstack/react-router"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table"
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "@/components/ui/card"
import { useApiQuery } from "@/api/ApiQueryServiceProvider"
import { Search, Edit, Users, ChevronLeft, ChevronRight, Loader2, Plus } from "lucide-react"

// No backend-generated schema exists for groups, so the row type is defined
// locally (RoleList infers its row type from GetRolesRegistry — there is no
// GetGroupsRegistry).
type Group = {
  groupNumber: number
  namespace: string
  organization: string
  groupName: string
  groupDescription?: string
  roles?: string[]
  members?: string[]
  groupUri?: string
}

export function GroupList() {
  const [searchQuery, setSearchQuery] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [orgFilter, setOrgFilter] = useState("")
  const itemsPerPage = 10

  const { data, isLoading, isError, refetch } = useApiQuery("getGroups", {})

  const groups: Group[] = ((data as any)?.content?.groups || (data as any)?.groups || []).map((g: any) => ({
    groupNumber: g.groupNumber,
    namespace: g.namespace,
    organization: g.organization,
    groupName: g.groupName,
    groupDescription: g.groupDescription ?? '',
    roles: g.roles ?? [],
    members: g.members ?? [],
    groupUri: g.groupUri ?? '',
  }))

  const filteredGroups = groups.filter((g) => {
    const matchesSearch = g.groupName?.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesOrg = !orgFilter || g.organization?.toLowerCase().includes(orgFilter.toLowerCase())
    return matchesSearch && matchesOrg
  })

  const totalPages = Math.ceil(filteredGroups.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const paginatedGroups = filteredGroups.slice(startIndex, startIndex + itemsPerPage)

  if (isLoading) {
    return (
      <Card className="shadow-sm">
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    )
  }

  if (isError) {
    return (
      <Card className="shadow-sm">
        <CardContent className="flex flex-col items-center justify-center py-12 gap-4">
          <p className="text-destructive">Failed to load groups</p>
          <Button variant="outline" onClick={() => refetch()}>Try Again</Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <Card className="shadow-sm pb-0">
        <CardHeader className="border-b bg-slate-700 text-white [&_p]:text-slate-300">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-xl">Manage Groups</CardTitle>
                <CardDescription className="text-base">Organize users into RBAC groups assigned roles</CardDescription>
              </div>
            </div>
            <Link to="/rema/groups/add">
              <Button size="sm" className="gap-2">
                <Plus className="h-4 w-4" />
                Add Group
              </Button>
            </Link>
          </div>
        </CardHeader>
      </Card>

      <Card className="shadow-sm pb-0">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            <div className="relative max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search groups..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1) }}
                className="pl-9"
              />
            </div>
            <div className="flex flex-wrap gap-2 items-center">
              <Input
                placeholder="Filter by organization..."
                value={orgFilter}
                onChange={(e) => { setOrgFilter(e.target.value); setCurrentPage(1) }}
                className="max-w-xs"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm pb-0">
        <CardContent className="p-4">
          <Table className="text-base">
            <TableHeader>
              <TableRow className="bg-muted/30 hover:bg-muted/30">
                <TableHead className="w-[80px] font-semibold text-base">ID</TableHead>
                <TableHead className="font-semibold text-base">Name</TableHead>
                <TableHead className="font-semibold text-base">Organization</TableHead>
                <TableHead className="w-[110px] font-semibold text-base">Namespace</TableHead>
                <TableHead className="w-[90px] font-semibold text-base">Roles</TableHead>
                <TableHead className="w-[100px] font-semibold text-base">Members</TableHead>
                <TableHead className="w-[90px] text-right font-semibold text-base">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedGroups.length > 0 ? (
                paginatedGroups.map((group) => (
                  <TableRow key={group.groupNumber} className="transition-colors hover:bg-muted/50">
                    <TableCell className="font-medium text-base">{group.groupNumber}</TableCell>
                    <TableCell className="font-medium text-base">
                      <Link
                        to="/rema/groups/$groupNumber"
                        params={{ groupNumber: String(group.groupNumber) }}
                        className="text-primary underline-offset-4 hover:underline"
                      >
                        {group.groupName}
                      </Link>
                    </TableCell>
                    <TableCell className="text-base text-muted-foreground">{group.organization || '—'}</TableCell>
                    <TableCell className="text-base font-mono">{group.namespace}</TableCell>
                    <TableCell className="text-base">{group.roles?.length ?? 0}</TableCell>
                    <TableCell className="text-base">{group.members?.length ?? 0}</TableCell>
                    <TableCell className="text-right">
                      <Button asChild variant="ghost" size="icon" className="h-8 w-8">
                        <Link to="/rema/groups/$groupNumber/edit" params={{ groupNumber: String(group.groupNumber) }}>
                          <Edit className="h-4 w-4" />
                          <span className="sr-only">Edit {group.groupName}</span>
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="h-24 text-center text-muted-foreground">
                    No groups found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t py-3">
              <p className="text-base text-muted-foreground">
                Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredGroups.length)} of {filteredGroups.length} groups
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" className="h-8 w-8" disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm font-medium">Page {currentPage} of {totalPages}</span>
                <Button variant="outline" size="icon" className="h-8 w-8" disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => p + 1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
